import { IsoWeek } from '../date/iso-week';
import { BusinessDate } from '../date/business-date';
import { daysBetween } from '../schedule/material-alert';
import type { ActivityId, MaterialId, ScheduleEntryId, UnitOfMeasureId } from '../shared/ids';

/**
 * O aviso de suprimentos (Fase 13).
 *
 * O sistema **não** gerencia compra: não há pedido, fornecedor, cotação nem estoque — essa
 * decisão é de 19/08/2026 e continua de pé, porque o ciclo de compra vive no sistema do
 * cliente e duplicá-lo aqui criaria duas verdades sobre o mesmo pedido. O que existe aqui
 * é um aviso, em três listas, e um único fato novo: **ciência**.
 *
 * Duas escolhas moram neste arquivo, e as duas foram feitas contra a intuição.
 *
 * **A lista é de necessidade, não de compra (RN-141).** O cronograma diz a data em que o
 * material precisa estar na obra; ele não diz quando comprar. Comprar acontece antes —
 * cotação, fabricação, entrega —, e cada material tem seu prazo. Uma lista de compra
 * derivada da data de necessidade faria tudo chegar atrasado exatamente pelo prazo de
 * entrega, e pareceria certa o tempo todo. Como o sistema não conhece esse prazo, ele diz
 * o que sabe e a conta da antecedência fica com quem compra.
 *
 * **Ciência não é recebimento.** Quem dá ciência declara que viu e vai providenciar. O
 * item sai da lista e não volta. O sistema nunca saberá se o material chegou — e portanto
 * a terceira seção lista **o que ninguém leu**, não o que faltou. É o preço de não ter a
 * máquina de status, e está escrito para não virar descoberta.
 */

/** Quantas semanas para trás a terceira seção enxerga. */
export const OVERDUE_LOOKBACK_WEEKS = 2;

export type SupplySection = 'THIS_WEEK' | 'NEXT_WEEK' | 'UNACKNOWLEDGED';

export const SECTION_LABEL: Readonly<Record<SupplySection, string>> = {
  THIS_WEEK: 'Precisa estar na obra nesta semana',
  NEXT_WEEK: 'Precisa estar na obra na próxima semana',
  UNACKNOWLEDGED: 'Semanas anteriores, ainda sem ciência',
};

/** Uma linha do cronograma: o material indo para uma frente de serviço específica. */
export interface SupplyLine {
  readonly entryId: ScheduleEntryId;
  readonly activityId: ActivityId;
  readonly quantity: number;
  readonly requiredDate: string;
  /**
   * Dias até a data de necessidade; negativo quando já passou.
   *
   * Sai daqui e não da tela porque o "hoje" que vale é o **da obra**, no fuso dela
   * (RN-123) — a tela não tem como saber disso sem refazer a conta e errar o fuso.
   */
  readonly daysUntil: number;
}

/**
 * O material como quem compra o enxerga: um item, uma quantidade.
 *
 * Seis linhas do mesmo tijolo em seis pavimentos são **um** pedido, não seis avisos. As
 * linhas continuam listadas abaixo do total porque a pergunta seguinte de quem compra é
 * sempre "para onde vai?" — mas a ciência é dada no material, de uma vez.
 */
export interface SupplyGroup {
  readonly materialId: MaterialId;
  readonly unitId: UnitOfMeasureId;
  readonly totalQuantity: number;
  /** A data mais apertada do grupo — é ela que ordena a lista. */
  readonly earliestRequiredDate: string;
  readonly lines: readonly SupplyLine[];
}

export interface SupplySectionView {
  readonly section: SupplySection;
  readonly groups: readonly SupplyGroup[];
  /** Quantas linhas de cronograma a seção representa, somando os grupos. */
  readonly lineCount: number;
}

export interface SupplyDigest {
  readonly isoYear: number;
  readonly isoWeek: number;
  readonly sections: readonly SupplySectionView[];
  /** Total de materiais aguardando ciência nas três seções — é o número do contador. */
  readonly pendingMaterials: number;
}

/** O que a função precisa saber de cada item do cronograma. */
export interface SupplyEntry {
  readonly id: ScheduleEntryId;
  readonly activityId: ActivityId;
  readonly materialId: MaterialId;
  readonly unitId: UnitOfMeasureId;
  readonly quantity: number;
  readonly requiredDate: string;
  readonly deletedAt?: string;
}

export interface SupplyDigestInput {
  readonly entries: readonly SupplyEntry[];
  /** Ids de item que já receberam ciência. */
  readonly acknowledgedEntryIds: ReadonlySet<string>;
  /** O "hoje" da obra, no fuso da obra (RN-123). É ele que produz "em 6 dias". */
  readonly today: BusinessDate;
  /**
   * A semana que a tela está olhando. Ausente = a de hoje.
   *
   * Separada do `today` de propósito. Ao navegar para a semana que vem, o recorte das
   * seções muda, mas "venceu há 14 dias" continua contando a partir de hoje — que é o
   * que a frase quer dizer para quem lê. Um prazo relativo a uma data escolhida no
   * seletor não seria prazo nenhum.
   */
  readonly reference?: BusinessDate;
  readonly lookbackWeeks?: number;
}

/**
 * Monta as três listas.
 *
 * Função pura sobre itens já carregados: quem lê do banco é o caso de uso. É o que permite
 * o mesmo cálculo servir a tela, ao e-mail semanal e ao teste, sem uma segunda conta em
 * lugar nenhum.
 */
export function buildSupplyDigest(input: SupplyDigestInput): SupplyDigest {
  const currentWeek = IsoWeek.of(input.reference ?? input.today);
  const nextWeek = currentWeek.next();

  const lookback = input.lookbackWeeks ?? OVERDUE_LOOKBACK_WEEKS;
  // O retrovisor tem fundo: item de um ano atrás sem ciência é história, não pendência, e
  // uma lista que só cresce deixa de ser lida em três meses.
  let oldest = currentWeek;
  for (let step = 0; step < lookback; step += 1) oldest = oldest.previous();

  const thisWeek: SupplyEntry[] = [];
  const upcoming: SupplyEntry[] = [];
  const pending: SupplyEntry[] = [];

  for (const entry of input.entries) {
    if (entry.deletedAt !== undefined) continue;

    const date = BusinessDate.parse(entry.requiredDate);
    /* c8 ignore next -- a data foi validada ao gravar o item (RN-021) */
    if (!date.ok) continue;

    // RN-143 — item com ciência sai das três listas e não volta, nem se a data mudar.
    const acknowledged = input.acknowledgedEntryIds.has(entry.id);

    if (currentWeek.contains(date.value)) {
      if (!acknowledged) thisWeek.push(entry);
      continue;
    }

    if (nextWeek.contains(date.value)) {
      if (!acknowledged) upcoming.push(entry);
      continue;
    }

    const week = IsoWeek.of(date.value);
    const isPast = week.compare(currentWeek) < 0;
    const withinLookback = week.compare(oldest) >= 0;
    if (isPast && withinLookback && !acknowledged) pending.push(entry);
  }

  const sections: SupplySectionView[] = [
    sectionOf('THIS_WEEK', thisWeek, input.today),
    sectionOf('NEXT_WEEK', upcoming, input.today),
    sectionOf('UNACKNOWLEDGED', pending, input.today),
  ];

  return {
    isoYear: currentWeek.isoYear,
    isoWeek: currentWeek.isoWeek,
    sections,
    pendingMaterials: sections.reduce((total, view) => total + view.groups.length, 0),
  };
}

function sectionOf(
  section: SupplySection,
  entries: readonly SupplyEntry[],
  today: BusinessDate,
): SupplySectionView {
  const groups = groupByMaterial(entries, today);
  return {
    section,
    groups,
    lineCount: groups.reduce((total, group) => total + group.lines.length, 0),
  };
}

/**
 * Junta as linhas do mesmo material.
 *
 * A unidade vem da primeira linha do grupo: itens do mesmo material no mesmo cronograma
 * carregam a unidade do catálogo (RN-011), então elas não divergem. Se um dia divergirem,
 * a soma passa a estar errada — e é por isso que a verificação de integridade existe.
 */
function groupByMaterial(entries: readonly SupplyEntry[], today: BusinessDate): SupplyGroup[] {
  const byMaterial = new Map<string, SupplyEntry[]>();
  for (const entry of entries) {
    const list = byMaterial.get(entry.materialId);
    if (list === undefined) byMaterial.set(entry.materialId, [entry]);
    else list.push(entry);
  }

  const groups: SupplyGroup[] = [];
  for (const [materialId, list] of byMaterial) {
    const lines = [...list]
      .sort((a, b) => a.requiredDate.localeCompare(b.requiredDate))
      .map((entry): SupplyLine => {
        const date = BusinessDate.parse(entry.requiredDate);
        return {
          entryId: entry.id,
          activityId: entry.activityId,
          quantity: entry.quantity,
          requiredDate: entry.requiredDate,
          /* c8 ignore next -- a data foi validada ao gravar o item (RN-021) */
          daysUntil: date.ok ? daysBetween(today, date.value) : 0,
        };
      });

    groups.push({
      materialId: materialId as MaterialId,
      unitId: list[0]!.unitId,
      totalQuantity: list.reduce((total, entry) => total + entry.quantity, 0),
      earliestRequiredDate: lines[0]!.requiredDate,
      lines,
    });
  }

  // O mais apertado primeiro: a lista existe para provocar ação, e a ação começa pelo que
  // vence antes.
  return groups.sort(
    (a, b) =>
      a.earliestRequiredDate.localeCompare(b.earliestRequiredDate) ||
      a.materialId.localeCompare(b.materialId),
  );
}

/** Todos os itens de um grupo — é o que a ciência de um clique grava (RN-142). */
export function entryIdsOf(group: SupplyGroup): ScheduleEntryId[] {
  return group.lines.map((line) => line.entryId);
}
